import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { useSession } from '../lib/session';

const seenKey = (userId: string) => `crm.intro.seen.${userId}`;

/**
 * A short animated welcome, shown once per person in this browser: the Danat mark draws itself, three cards say
 * where things are, and "Watch the video tour" opens the tour for their role. Not while an administrator uses
 * "Log in as", and not on the guide itself.
 */
export function WelcomeIntro() {
  const { user, impersonator } = useSession();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(() => {
    try { return !localStorage.getItem(seenKey(user.id)); } catch { return false; }
  });
  const show = open && !impersonator && !location.pathname.startsWith('/help');
  const close = () => { setOpen(false); try { localStorage.setItem(seenKey(user.id), '1'); } catch { /* not remembered */ } };
  useEffect(() => {
    if (!show) return;
    // Escape closes only this welcome, not a pop-up waiting behind it.
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.stopPropagation(); close(); } };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  });
  if (!show) return null;
  const first = user.name.split(' ')[0];
  return (
    <div className="modal-backdrop intro-backdrop" onClick={(e) => { if (e.target === e.currentTarget) close(); }}>
      <div className="modal intro" role="dialog" aria-modal="true" aria-labelledby="intro-title">
        <svg className="intro-mark" viewBox="0 0 120 80" aria-hidden="true">
          <path pathLength={1} d="M12 10h22c18 0 28 12 28 30s-10 30-28 30H12z" />
          <path pathLength={1} d="M66 10h24c12 0 18 6 18 15 0 7-4 12-10 14 8 2 13 8 13 16 0 10-7 15-20 15H66z" />
        </svg>
        <h2 id="intro-title">Welcome to Danat Business ERP, {first}</h2>
        <p className="muted">Everything for your deals, quotations and projects in one place.</p>
        <ol className="intro-cards">
          <li><b>Home</b><span>What needs you today: attention, tasks and your goal.</span></li>
          <li><b>Your work</b><span>Deals, pre-sales and projects, each with its own workspace.</span></li>
          <li><b>Help</b><span>The <b>?</b> menu has a video tour you can watch or try yourself.</span></li>
        </ol>
        <div className="intro-actions">
          <button type="button" className="btn" onClick={close}>Let’s start</button>
          <button type="button" className="btn primary" autoFocus onClick={() => { close(); navigate('/help?tour=play'); }}>▶ Watch the video tour</button>
        </div>
      </div>
    </div>
  );
}
